// All three are `import type` so this module stays free of runtime imports: its sort-hash
// converters are pure, and a value import would drag React, MUI and the store graph in behind
// them — which is what put `usePinnedFirstToggle` in `hooks/` rather than here.
import type { GridSortModel } from '@components/DataTable';
import type { SortOptionItem as MobileSortOptionItem } from '@components/MobileSortDrawer/MobileSortDrawer';
import type { LibrarySortBy, LibrarySortByHash } from '@stores/LibraryManager/LibraryManager';

/**
 * Label for the pinned-first modifier, shared by the desktop menu row, the mobile drawer row
 * and the suffix on the desktop button face — so the control and the state it reports back
 * can't be worded differently.
 */
export const PINNED_FIRST_LABEL = 'Pinned first';

/**
 * Canonical sort option groups for the Libraries page, used by both the desktop
 * dropdown (LibrariesNav → SortButton) and the mobile sort drawer
 * (Mobile/MobileSortDrawer). Keep additions/edits here so both stay in sync.
 *
 * Mirrors `src/pages/Library/utils/sortOptions.ts`, which does the same job for
 * library *media*. Previously these groups were declared twice — once per
 * consumer, in two different shapes — and kept in sync by hand.
 *
 * Direction semantics follow `LibraryManager.librarySections`:
 * `#1` sorts descending for numeric/date fields (newest, longest, most) and
 * ascending for title (A to Z); `#-1` reverses each.
 */
export const LIBRARY_SORT_GROUPS: { value: LibrarySortByHash; label: string }[][] = [
  [
    { value: 'title#1', label: 'Title, A to Z' },
    { value: 'title#-1', label: 'Title, Z to A' },
  ],
  [
    { value: 'modified#-1', label: 'First modified' },
    { value: 'modified#1', label: 'Last modified' },
  ],
  [
    { value: 'added#-1', label: 'First added' },
    { value: 'added#1', label: 'Last added' },
  ],
  [
    { value: 'duration#-1', label: 'Shortest' },
    { value: 'duration#1', label: 'Longest' },
  ],
  [
    { value: 'count#-1', label: 'Fewest media' },
    { value: 'count#1', label: 'Most media' },
  ],
  // Listed even though sorting libraries by storage is a rare need, because the
  // desktop table's Size header can set it: an unlisted sort is one both pickers
  // render as "nothing selected", leaving the user unable to see the active sort or
  // change it from mobile at all.
  [
    { value: 'size#-1', label: 'Smallest' },
    { value: 'size#1', label: 'Largest' },
  ],
];

/** Flat lookup: sort hash → display label. */
export const LIBRARY_SORT_OPTION_LABELS: Record<string, string> = Object.fromEntries(
  LIBRARY_SORT_GROUPS.flat().map((opt) => [opt.value, opt.label]),
);

/**
 * What each field's `#1` direction actually means in
 * `LibraryManager.sortedAndFilteredLibraries`. The numeric and date fields sort
 * `b - a`, so `#1` puts the newest / longest / most / largest first; `title` sorts
 * `localeCompare(a, b)`, so `title#1` is A→Z. The converters below can't simply map
 * `1 → 'desc'` because of that asymmetry.
 *
 * Typed as a total `Record` over `LibrarySortBy` on purpose: a new sort field is
 * then a compile error here rather than a header arrow that silently points the
 * wrong way. (The real fix is for the store to expose a normalized direction, but
 * the hash is persisted to localStorage, so changing its semantics would invert
 * every existing user's saved sort.)
 */
const SORT_DIRECTION_OF_POSITIVE: Record<LibrarySortBy, 'desc' | 'asc'> = {
  title: 'asc',
  modified: 'desc',
  added: 'desc',
  duration: 'desc',
  count: 'desc',
  size: 'desc',
};

/** Store sort hash → the DataGrid sort model the table's headers render from. */
export function librarySortHashToGridModel(hash: LibrarySortByHash): GridSortModel {
  const [field, rawDirection] = hash.split('#') as [LibrarySortBy, string];
  const positiveMeans = SORT_DIRECTION_OF_POSITIVE[field];
  const inverse = positiveMeans === 'desc' ? 'asc' : 'desc';
  return [{ field, sort: rawDirection === '1' ? positiveMeans : inverse }];
}

/**
 * DataGrid sort model → store sort hash. Returns `null` when the model is empty
 * or has no direction, which the caller should treat as "leave the sort alone"
 * rather than resetting it.
 */
export function gridSortModelToLibrarySortHash(model: GridSortModel): LibrarySortByHash | null {
  const entry = model[0];
  if (!entry?.sort) return null;
  const field = entry.field as LibrarySortBy;
  const positiveMeans = SORT_DIRECTION_OF_POSITIVE[field];
  if (!positiveMeans) return null;
  return `${field}#${entry.sort === positiveMeans ? 1 : -1}`;
}

/** Label for the current selection, for the desktop button face. */
export function getLibrarySortLabel(value: string): string {
  return LIBRARY_SORT_OPTION_LABELS[value] ?? 'Sort';
}

/**
 * Groups shaped for `MobileSortDrawer`, which needs a separate `key`. The hash
 * is unique per option, so it doubles as the key.
 */
export const LIBRARY_SORT_GROUPS_MOBILE: MobileSortOptionItem<LibrarySortByHash>[][] =
  LIBRARY_SORT_GROUPS.map((group) =>
    group.map(({ value, label }) => ({ key: value, label, value })),
  );
